"use client";

import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { NotesTab } from './collaboration/NotesTab';
import { FilesTab } from './collaboration/FilesTab';
import { MessageSquare, FileIcon, } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import NoteService from '@/api/note.service';
import FileService from '@/api/file.service';
import UserService from '@/api/users.service';

interface CollaborationTabsProps {
  customerId: number;
  entityType: 'customer' | 'deal';
  entityId: number;
}

export function CollaborationTabs({ customerId, entityType, entityId }: CollaborationTabsProps) {
  const [notes, setNotes] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchNotes = async () => {
      setIsLoadingNotes(true);
      try {
        let response;
        if (entityType === 'customer') {
          response = await NoteService.getNotesByCustomer(entityId);
        } else {
          response = await NoteService.getNotesByDeal(entityId);
        }
        
        if (response.data.result === 'success') {
          setNotes(response.data.notes || []);
        } else if (response.data.result === 'invalid') {
          window.location.href = '/logout';
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load notes",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching notes:", error);
        toast({
          title: "Error",
          description: "Failed to load notes. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingNotes(false);
      }
    };

    const fetchFiles = async () => {
      setIsLoadingFiles(true);
      try {
        let response;
        if (entityType === 'customer') {
          response = await FileService.getFilesByCustomer(entityId);
        } else {
          response = await FileService.getFilesByDeal(entityId);
        }
        
        if (response.data.result === 'success') {
          setFiles(response.data.files || []);
        } else if (response.data.result === 'invalid') {
          window.location.href = '/logout';
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load files",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching files:", error);
        toast({
          title: "Error",
          description: "Failed to load files. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingFiles(false);
      }
    };
    
    const fetchUsers = async () => {
      if (!selectedBusiness?.id) return;
      
      setIsLoadingUsers(true);
      try {
        const response = await UserService.getTeamUsers(selectedBusiness.id);
        if (response.data.result === 'success') {
          setUsers(response.data.users || []);
        } else if (response.data.result === 'invalid') {
          window.location.href = '/logout';
        }
      } catch (error) {
        console.error("Error fetching team users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchNotes();
    fetchFiles();
    fetchUsers();
  }, [entityId, entityType, toast, selectedBusiness]);

  const handleNoteAdded = (newNote: any) => {
    setNotes((prevNotes) => [newNote, ...prevNotes]);
  };

  const handleNoteDeleted = (noteId: number) => {
    setNotes((prevNotes) => prevNotes.filter(note => note.id !== noteId));
  };

  const handleFileAdded = (newFile: any) => {
    setFiles((prevFiles) => [newFile, ...prevFiles]);
  };

  const handleFileDeleted = (fileId: number) => {
    setFiles((prevFiles) => prevFiles.filter(file => file.id !== fileId));
  };

  return (
    <Card className="shadow-sm border-slate-200 dark:border-slate-800">
      <Tabs defaultValue="notes" className="w-full">
        <TabsList className="flex justify-start px-6 pt-4 border-b w-full bg-transparent h-auto gap-4">
          <TabsTrigger 
            value="notes" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none pb-3 px-2"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Notes {notes.length > 0 && <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{notes.length}</span>}
          </TabsTrigger>
          <TabsTrigger 
            value="files" 
            className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none pb-3 px-2"
          >
            <FileIcon className="w-4 h-4 mr-2" />
            Files {files.length > 0 && <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{files.length}</span>}
          </TabsTrigger>
        </TabsList>
        
        <CardContent className="p-0">
          <TabsContent value="notes" className="m-0">
            <NotesTab 
              notes={notes} 
              users={users}
              entityType={entityType}
              entityId={entityId}
              businessId={selectedBusiness?.id}
              isLoading={isLoadingNotes || isLoadingUsers}
              onNoteAdded={handleNoteAdded}
              onNoteDeleted={handleNoteDeleted}
            />
          </TabsContent>
          
          <TabsContent value="files" className="m-0">
            <FilesTab 
              files={files}
              entityType={entityType}
              entityId={entityId}
              businessId={selectedBusiness?.id}
              isLoading={isLoadingFiles}
              onFileAdded={handleFileAdded}
              onFileDeleted={handleFileDeleted}
            />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
} 